import React from 'react';
import { render, screen } from '@testing-library/react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CandidatesTableRows from '../CandidatesTableRows';
import { TCandidate } from '../types';

// Mock the GenericTable components
jest.mock('../../Components', () => ({
  StyledSnackbar: ({ message, state }: { message: string; state: string }) => (
    <div data-testid="snackbar" data-state={state}>
      {message}
    </div>
  ),
  ISnackbarInput: {},
  formatUserPermissions: () => ({
    Candidates: {
      'Edit Candidates': true
    }
  }),
  GenericTable: ({
    items,
    TableActionList,
    onActionItemClick
  }: {
    items: any[];
    TableActionList: any[];
    onActionItemClick: (action: any, item: any) => void;
  }) => (
    <div data-testid="generic-table">
      {items?.map((item: any, index: number) => (
        <div key={item.id || index} data-testid={`candidate-${item.id || index}`}>
          <span data-testid={`candidate-name-${item.id}`}>
            {item.firstname} {item.lastname}
          </span>
          <span data-testid={`candidate-email-${item.id}`}>{item.email}</span>
          <span data-testid={`candidate-phone-${item.id}`}>{item.phone1}</span>
          <span data-testid={`candidate-applications-${item.id}`}>
            {item.number_of_applications}
          </span>
          {TableActionList?.map((action: any) => (
            <button
              key={action.id}
              data-testid={`action-${action.id}-${item.id}`}
              onClick={() => onActionItemClick?.(action, item)}
            >
              {action.label}
            </button>
          ))}
        </div>
      ))}
    </div>
  )
}));

// Mock the TagList component
jest.mock('../../Components', () => ({
  ...jest.requireActual('../../Components'),
  TagList: ({ tags }: { tags: any[] }) => (
    <div data-testid="tag-list">
      {tags?.map((tag: any, index: number) => (
        <span key={index} data-testid={`tag-${index}`}>
          {typeof tag === 'string' ? tag : tag.label || tag.value}
        </span>
      ))}
    </div>
  )
}));

const theme = createTheme();

const renderWithTheme = (component: React.ReactElement) => {
  return render(<ThemeProvider theme={theme}>{component}</ThemeProvider>);
};

describe('CandidatesTableRows', () => {
  const mockCandidates: TCandidate[] = [
    {
      id: 1,
      firstname: 'John',
      lastname: 'Doe',
      email: 'john.doe@example.com',
      phone1: '1234567890',
      phone2: '0987654321',
      number_of_applications: 2,
      employee_id: null,
      duplicated_candidates: [],
      existing_candidate: false,
      gender_full: 'Male',
      updated_at: '2023-01-01T00:00:00Z',
      linkedin_url: null,
      address: '123 Main St',
      tag_list: ['experienced', 'senior'],
      entity_tags: [
        { value: 'senior', label: 'Senior' },
        { value: 'experienced', label: 'Experienced' }
      ],
      address_details: {
        country: { id: 1, name: 'Australia', iso: 'AU' },
        state: { id: 1, name: 'NSW' },
        id: 1,
        postcode: '2000',
        street1: '123 Main St',
        street2: null,
        street_number: '123',
        street_type: 'St',
        suburb: 'Sydney'
      },
      notepad: { body: 'Great candidate' },
      title: 'Software Engineer'
    },
    {
      id: 2,
      firstname: 'Jane',
      lastname: 'Smith',
      email: 'jane.smith@example.com',
      phone1: '0987654321',
      phone2: '',
      number_of_applications: 1,
      employee_id: 'EMP001',
      duplicated_candidates: [],
      existing_candidate: true,
      gender_full: 'Female',
      updated_at: '2023-01-02T00:00:00Z',
      linkedin_url: 'https://linkedin.com/in/janesmith',
      address: '456 Oak Ave',
      tag_list: [],
      entity_tags: [],
      address_details: {
        country: { id: 1, name: 'Australia', iso: 'AU' },
        state: { id: 2, name: 'VIC' },
        id: 2,
        postcode: '3000',
        street1: '456 Oak Ave',
        street2: null,
        street_number: '456',
        street_type: 'Ave',
        suburb: 'Melbourne'
      },
      notepad: null,
      title: 'Senior Developer'
    }
  ];

  const mockTableState = {
    canScroll: [false, true],
    tableHovered: false,
    density: 'Default',
    columns: [
      { id: 'id', label: 'ID', sortable: true, canToggle: false, enabled: true },
      { id: 'firstname', label: 'First Name', sortable: true, canToggle: true, enabled: true },
      { id: 'lastname', label: 'Last Name', sortable: true, canToggle: true, enabled: true },
      { id: 'email', label: 'Email', sortable: true, canToggle: true, enabled: true },
      { id: 'phone1', label: 'Phone', sortable: true, canToggle: true, enabled: true },
      { id: 'applications', label: 'Applications', sortable: true, canToggle: true, enabled: true }
    ],
    selectedItemIds: [],
    selectedItems: [],
    actions: {
      selectedItem: null,
      anchorEl: null,
      isOpen: {
        delete: false,
        edit: false
      }
    },
    modalsOpen: { edit: false }
  };

  beforeEach(() => {
    jest.clearAllMocks();
  });

  it('renders candidates data correctly', () => {
    renderWithTheme(
      <table>
        <tbody>
          {mockCandidates.map((candidate) => (
            <tr key={candidate.id}>
              {mockTableState.columns.map((column) => (
                <CandidatesTableRows
                  key={`${candidate.id}-${column.id}`}
                  column={column}
                  item={candidate}
                  tableHovered={false}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );

    expect(screen.getByText('John')).toBeInTheDocument();
    expect(screen.getByText('Doe')).toBeInTheDocument();
    expect(screen.getByText('john.doe@example.com')).toBeInTheDocument();
    expect(screen.getByText('1234567890')).toBeInTheDocument();
    expect(screen.getAllByText('2')).toHaveLength(2); // ID and applications count

    expect(screen.getByText('Jane')).toBeInTheDocument();
    expect(screen.getByText('Smith')).toBeInTheDocument();
    expect(screen.getByText('jane.smith@example.com')).toBeInTheDocument();
    expect(screen.getByText('0987654321')).toBeInTheDocument();
    expect(screen.getAllByText('1')).toHaveLength(2); // ID and applications count
  });

  it('handles empty candidates array', () => {
    renderWithTheme(
      <table>
        <tbody>
          {[].map((candidate) => (
            <tr key={candidate.id}>
              {mockTableState.columns.map((column) => (
                <CandidatesTableRows
                  key={`${candidate.id}-${column.id}`}
                  column={column}
                  item={candidate}
                  tableHovered={false}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );

    // No candidates should render
    expect(screen.queryByText('John Doe')).not.toBeInTheDocument();
  });

  it('renders individual table cells correctly', () => {
    const candidate = mockCandidates[0];
    const column = mockTableState.columns[0]; // ID column

    renderWithTheme(
      <table>
        <tbody>
          <tr>
            <CandidatesTableRows column={column} item={candidate} tableHovered={false} />
          </tr>
        </tbody>
      </table>
    );

    expect(screen.getByText('1')).toBeInTheDocument(); // Candidate ID
  });

  it('displays candidate information correctly', () => {
    const candidate = mockCandidates[0];

    renderWithTheme(
      <table>
        <tbody>
          <tr>
            {mockTableState.columns.map((column) => (
              <CandidatesTableRows
                key={`${candidate.id}-${column.id}`}
                column={column}
                item={candidate}
                tableHovered={false}
              />
            ))}
          </tr>
        </tbody>
      </table>
    );

    expect(screen.getByText('John')).toBeInTheDocument();
    expect(screen.getByText('Doe')).toBeInTheDocument();
    expect(screen.getByText('john.doe@example.com')).toBeInTheDocument();
    expect(screen.getByText('1234567890')).toBeInTheDocument();
    expect(screen.getByText('2')).toBeInTheDocument();
  });

  it('handles candidates with missing optional fields', () => {
    const candidateWithMissingFields: TCandidate = {
      id: 3,
      firstname: 'Bob',
      lastname: 'Johnson',
      email: 'bob@example.com',
      phone1: '5555555555',
      phone2: '',
      number_of_applications: 0,
      employee_id: null,
      duplicated_candidates: [],
      existing_candidate: false,
      gender_full: null,
      updated_at: '2023-01-03T00:00:00Z',
      linkedin_url: null,
      address: '',
      tag_list: [],
      entity_tags: [],
      address_details: {
        country: null,
        state: null,
        id: 3,
        postcode: null,
        street1: null,
        street2: null,
        street_number: null,
        street_type: null,
        suburb: null
      },
      notepad: null,
      title: null
    };

    renderWithTheme(
      <table>
        <tbody>
          <tr>
            {mockTableState.columns.map((column) => (
              <CandidatesTableRows
                key={`${candidateWithMissingFields.id}-${column.id}`}
                column={column}
                item={candidateWithMissingFields}
                tableHovered={false}
              />
            ))}
          </tr>
        </tbody>
      </table>
    );

    expect(screen.getByText('Bob')).toBeInTheDocument();
    expect(screen.getByText('Johnson')).toBeInTheDocument();
    expect(screen.getByText('bob@example.com')).toBeInTheDocument();
    expect(screen.getByText('5555555555')).toBeInTheDocument();
    expect(screen.getByText('3')).toBeInTheDocument(); // Candidate ID
    expect(screen.getByText('-')).toBeInTheDocument(); // Applications count (shows "-" for 0)
  });
});
